import { LocalDateTime } from '@js-joda/core'
import { SttRemark } from '../../../types'
import { SttRemarkUtils } from './SttRemarkUtils'

export interface SttRemarkWithMetadata extends Omit<SttRemark, 'content'> {
  /**
   * We Improve readability by inserting a line break every 5 sentences in long remarks with > 8 sentences
   */
  content: string[]
  isFirstRemarkOfSpeaker: boolean
  isFirstRemarkOfMinute: boolean
  followsSealedContentMarker?: boolean
  canViewSealedContent?: boolean
  id: string
  segmentId: string
}

export interface SttRemarkWithPlayerInformation extends SttRemarkWithMetadata {
  isRemarkBeingPlayed: boolean

  /**
   * Will only be true if:
   *
   * A. Its the first remark in the speaker.
   * B. One of the remarks in this speaker is currently playing
   */
  isSpeakerActive: boolean

  /**
   * Remark is during the time range value provided in the sealing toolbar
   */
  toBeSealed: boolean
}

export class SttRemarkWithMetadataBuilder {
  static buildFromRemarks(segmentId: string, remarks: SttRemark[]): SttRemarkWithMetadata[] {
    return remarks.map((_, idx) => this.buildFromRemark(idx, segmentId, remarks))
  }

  /**
   * Regeneration requires the remarks to be in order
   */
  static regenerateMetadata(remarks: SttRemarkWithMetadata[]): SttRemarkWithMetadata[] {
    return remarks.map((remark, idx) => ({
      ...remark,
      isFirstRemarkOfMinute: SttRemarkUtils.isFirstRemarkOfMinute(remarks[idx], remarks[idx - 1]),
      isFirstRemarkOfSpeaker: SttRemarkUtils.isFirstRemarkOfSpeaker(remarks[idx], remarks[idx - 1]),
    }))
  }

  static buildFromRemark(idx: number, segmentId: string, remarks: SttRemark[]): SttRemarkWithMetadata {
    const r = remarks[idx]
    const content = r.content ? [r.content] : []

    return {
      endTime: r.endTime,
      speakerId: r.speakerId,
      speakerName: r.speakerName,
      startTime: r.startTime,
      status: r.status,
      id: SttRemarkUtils.generateId(),
      segmentId,
      isFirstRemarkOfMinute: SttRemarkUtils.isFirstRemarkOfMinute(remarks[idx], remarks[idx - 1]),
      isFirstRemarkOfSpeaker: SttRemarkUtils.isFirstRemarkOfSpeaker(remarks[idx], remarks[idx - 1]),
      content,
      isSealed: r.isSealed,
      isSealedContentMarker: r.isSealedContentMarker,
      grouping: r.grouping,
    }
  }

  /**
   * The start/end time is the piece we want to "cut out" of the remark. This happens when new content comes in from
   * a socket event, replacing content in the segment map between the start/end time.
   *
   * There are 4 cases here:
   * 1. We want to delete the entire remark
   * 2. We want to move the remark start time to the end time
   * 3. We want to move the remark end time to the start time
   * 4. We want to split the remark into 2 pieces, and take out a chunk in the middle
   */
  static shrinkSplitOrRemove(
    startTime: LocalDateTime,
    endTime: LocalDateTime,
    remark: SttRemarkWithMetadata,
    toleranceMs: number = 0,
  ): SttRemarkWithMetadata[] {
    if (SttRemarkUtils.isWithin(remark, startTime, endTime, toleranceMs)) {
      return []
    } else if (SttRemarkUtils.startsBeforeAndEndsDuring(remark, startTime, endTime, toleranceMs)) {
      return [SttRemarkWithMetadataBuilder.withEndTime(remark, startTime)]
    } else if (SttRemarkUtils.startsDuringAndEndsAfter(remark, startTime, endTime, toleranceMs)) {
      return [SttRemarkWithMetadataBuilder.withStartTime(remark, endTime)]
    } else if (SttRemarkUtils.startsBeforeAndEndsAfter(remark, startTime, endTime)) {
      // Note: We regenerate the ids when we split, otherwise we would have 2 remarks with the same id
      return [
        SttRemarkWithMetadataBuilder.withEndTime(remark, startTime, true),
        SttRemarkWithMetadataBuilder.withStartTime(remark, endTime, true),
      ]
    }

    // Probably wont happen, but just be safe.
    // This would only happen if the remark starts before and ends before the start/end time. Meaning it does not
    // need to be split or shrunk
    return [remark]
  }

  private static withEndTime(
    remark: SttRemarkWithMetadata,
    endTime: LocalDateTime,
    changeId: boolean = false,
  ): SttRemarkWithMetadata {
    return {
      ...remark,
      id: changeId ? SttRemarkUtils.generateId() : remark.id,
      endTime,
    }
  }

  private static withStartTime(
    remark: SttRemarkWithMetadata,
    startTime: LocalDateTime,
    changeId: boolean = false,
  ): SttRemarkWithMetadata {
    return {
      ...remark,
      id: changeId ? SttRemarkUtils.generateId() : remark.id,
      startTime,
    }
  }

  /**
   * Basically the opposite of shrinkSplitOrRemove. Expands this remark encompass the start/end time.
   *
   * We only ever expand the end, as the start time must remain the same (due to being in the correct segment)
   *
   * Essentially this means, we had some socket event that came in adjacent to this remark with the same status.
   *
   * Eg. 2 TRM's begun generating one after the other, so we create a single loading remark that encompasses the
   * time of both TRM's
   */
  static expand(remark: SttRemarkWithMetadata, endTime: LocalDateTime | undefined): SttRemarkWithMetadata {
    return SttRemarkWithMetadataBuilder.withEndTime(remark, endTime || remark.endTime)
  }

  static withSegmentId(remark: SttRemarkWithMetadata, segmentId: string): SttRemarkWithMetadata {
    return {
      ...remark,
      segmentId,
    }
  }
}
